import * as React from 'react';
import styled from 'styled-components';

import { getShadow, getZIndex } from '@process-advisor/process-miner/shared/theme';
import { SidebarTab } from '@process-advisor/process-miner/shared/consts';
import { apiProvider } from '@process-advisor/process-miner/api/apiProvider';
import { Event } from '@process-advisor/process-miner/api/events';
import { Tabs } from '@process-advisor/process-miner/navigation/sidebar/tabs/tabs';
import { VariantsSelector } from '@process-advisor/process-miner/slicers/variants/variants';
import { VariantsChart } from '@process-advisor/process-miner/slicers/variants/variantsChart';
import { ExpansionPanel } from '@process-advisor/process-miner/navigation/sidebar/expansionPanel/expansionPanel';
import { Discovery } from '@process-advisor/process-miner/discovery/discovery';
import { StepFilterTab } from '@process-advisor/process-miner/filters/steps/step';
import { VariantsFilter } from '@process-advisor/process-miner/filters/variants/variants';
import { ActivityFilterTab } from '@process-advisor/process-miner/filters/activities/activity';
import { PathFilterTab } from '@process-advisor/process-miner/filters/path/path';
import { TaskAttributesTab } from '@process-advisor/process-miner/filters/attributes/attributes';
import { MetricsTab } from '@process-advisor/process-miner/filters/metrics/metrics';
import { TimeTab } from '@process-advisor/process-miner/filters/time/time';
import { VisualizerSettings } from '@process-advisor/process-miner/settings/processMiner/processMiner';
import { SidebarPanel } from '@process-advisor/process-miner/navigation/sidebar/sidebarPanel';
import { useApiListener } from '@process-advisor/process-miner/shared/hooks/useApiListener';
import { SidebarLoader } from '@process-advisor/process-miner/components/sidebarLoader/sidebarLoader';
import { VisualizerOptions } from '@process-advisor/process-miner/navigation/sidebar/visualizerOptions';
import { WorkflowOptions } from '@process-advisor/process-miner/navigation/sidebar/workflowOptions';
import { Status } from '@process-advisor/process-miner/filters/status/status';
import { ActivityColors } from '@process-advisor/process-miner/settings/activityColors/activityColors'

const Wrapper = styled.div`
    margin-left: -4px;
    position: relative;
    overflow-y: auto;
    height: 100%;
    min-height: 0;
    background-color: #F5F5F5;
`;

const Content = styled.div<{ $initialLoad: boolean }>`
    width: ${({ $initialLoad }) => $initialLoad ? 'calc(100%);' : 'calc(100% - 50px);'};
    padding: 10px;
    display: flex;
    flex-direction: column;
    height: 100%;
    box-shadow: ${getShadow('panel')};
    position: relative;
`;

export const Sidebar = ({app,modelID,mode,processLabel='Process'})  =>  {

    const [initialLoad, setInitialLoad] = React.useState(true);
    const [activeTab, setActiveTab] = React.useState<SidebarTab>(SidebarTab.variantsTab);
    const [activityExpanded, setActivityExpanded] = React.useState(false);
    const [stepExpanded, setStepExpanded] = React.useState(false);
    const [filterExpanded, setFilterExpanded] = React.useState(false);
    const [discoveryExpanded, setDiscoveryExpanded] = React.useState(false);
    const [settingsExpanded, setSettingsExplanded]  = React.useState(false);

    const entityTitle = apiProvider.getEntityUpper() + 's'

    React.useEffect(() => {
        setInitialLoad(false)
    }, []);
    
    useApiListener(Event.setVariantPane, () => {
        setActiveTab(SidebarTab.variantsTab);
        setInitialLoad(false)
    });

    
    React.useEffect(() => {
        const listener = () => {
            setActiveTab(SidebarTab.discoveryTab);
            setDiscoveryExpanded(true)    
        }

        apiProvider.on(Event.setDiscoveryExpanded, listener);
        return () => {
            apiProvider.off(Event.setDiscoveryExpanded, listener);
        };
    }, []);

    React.useEffect(() => {
        const listener = () => {
            setActiveTab(SidebarTab.filtersTab);
            setFilterExpanded(true)
        }
        apiProvider.on(Event.setFilterExpanded, listener);
        return () => {
            apiProvider.off(Event.setFilterExpanded, listener);
        };
    }, []);

    React.useEffect(() => {
        const listener = () => { 
            setActiveTab(SidebarTab.settingsTab)   
            setSettingsExplanded(true)
        };    

        apiProvider.on(Event.openSettings, listener);
        return () => {
            apiProvider.off(Event.openSettings, listener);
        };
    }, []);


    React.useEffect(() => {
        const listener = () => { 
            setActiveTab(SidebarTab.filtersTab);
            setActivityExpanded(true)
        };    

        apiProvider.on(Event.openFilterActivity, listener);
        return () => {
            apiProvider.off(Event.openFilterActivity, listener);
        };
    }, []);

    React.useEffect(() => {
        const listener = () => { 
            setActiveTab(SidebarTab.filtersTab);
            setStepExpanded(true)
        };    

        apiProvider.on(Event.openFilterStep, listener);
        return () => {
            apiProvider.off(Event.openFilterStep, listener);
        };
    }, []);

    React.useEffect(() => {
        apiProvider.changeActivePane({ pane: activeTab });
    }, [activeTab]);
    
    return (
        <div>
        {mode === 'process_miner' ?
        <Wrapper>   
                <Tabs
                    onChange={setActiveTab}
                    mode={mode}
                    initialLoad={initialLoad}
                    activeTab={activeTab}/>
                <Content $initialLoad={initialLoad}>
                        <SidebarPanel activeTab={activeTab} tab={SidebarTab.variantsTab}>
                            <ExpansionPanel expanded={true} title='Variants Selection'>
                                <VariantsSelector mode={mode}/>
                            </ExpansionPanel>
                            <ExpansionPanel expanded={false} title='Variants Chart'>
                                <VariantsChart mode={mode}/>
                            </ExpansionPanel>
                            { mode === 'process_miner'?
                            <div>
                                { mode === 'process_miner' ?
                                    <ExpansionPanel expanded={true} title='Visualizer Options'>
                                        <VisualizerOptions app={app}/>
                                    </ExpansionPanel>
                                : 
                                    <ExpansionPanel expanded={true} title='Visualizer Options'>
                                        <WorkflowOptions/>
                                    </ExpansionPanel>
                                }
                            </div>
                            : ''}
                        </SidebarPanel>
                        <SidebarPanel activeTab={activeTab} tab={SidebarTab.filtersTab}>
                            <ExpansionPanel title='Status' >
                                <Status mode={mode} type={'filter'} />
                            </ExpansionPanel>
                            <ExpansionPanel title='Variants' >
                                <VariantsFilter  mode={mode} />
                            </ExpansionPanel>
                            <ExpansionPanel title='Activities' >
                                <ActivityFilterTab  mode={mode} expanded={activityExpanded}/>
                            </ExpansionPanel>
                            <ExpansionPanel  title='Steps' expanded={stepExpanded} >
                                <StepFilterTab mode={mode}/>
                            </ExpansionPanel>
                            <ExpansionPanel title='Path' >
                                <PathFilterTab  mode={mode} />
                            </ExpansionPanel>
                            <ExpansionPanel title='Attributes' >
                                <TaskAttributesTab mode={mode} />
                            </ExpansionPanel>
                            <ExpansionPanel title='Time' >
                                <TimeTab/>
                            </ExpansionPanel>
                        </SidebarPanel>          
                        <SidebarPanel activeTab={activeTab} tab={SidebarTab.discoveryTab}>
                            <ExpansionPanel title='Status'>
                                <Status type={'discovery'}   />
                            </ExpansionPanel>
                            <ExpansionPanel title='Activities'>
                                <Discovery type={'Activities'} />
                            </ExpansionPanel>                            
                            <ExpansionPanel title='Steps'>
                                <Discovery type={'Steps'} />
                            </ExpansionPanel>                            
                            <ExpansionPanel title='Variants'>
                                <Discovery type={'Variants'} />
                            </ExpansionPanel>                            
                        </SidebarPanel>
                        <SidebarPanel activeTab={activeTab} tab={SidebarTab.settingsTab}>
                            <ExpansionPanel expanded={true}
                                title={`${processLabel} Map`}>
                                <VisualizerSettings/>
                            </ExpansionPanel>
                            <ExpansionPanel expanded={false}
                                title='Activity Type Colors'>
                                <ActivityColors/>
                            </ExpansionPanel>
                        </SidebarPanel>
                </Content>
                <SidebarLoader />

        </Wrapper>
        : '' }
        </div>
    );
};


/*

                            <ExpansionPanel title='Metrics' >
                                <MetricsTab mode={mode} />
                            </ExpansionPanel>
*/                        